import { type Cell, type Sheet, cellKey, sheetNamesEqual, usedExtent } from "@package/scratchpad-utils";
import type { CellRect } from "./picking";
import { retargetRefs } from "./retarget";

export type MoveResult = {
  sheets: Sheet[];
  removals: string[];
  updates: Array<{ nodeId: string; src: string }>;
};

export function applyMoveRange(
  sheets: Sheet[],
  cutSheetId: string,
  rect: CellRect,
  dCol: number,
  dRow: number,
): MoveResult {
  const cutSheetName = sheets.find((s) => s.id === cutSheetId)?.name;
  if (!cutSheetName || (dCol === 0 && dRow === 0)) return { sheets, removals: [], updates: [] };
  const removals: string[] = [];
  const updates: Array<{ nodeId: string; src: string }> = [];
  const next = sheets.map((sheet) => {
    let cells = sheet.cells;
    if (sheet.id === cutSheetId) {
      cells = { ...cells };
      const moved: Array<[string, Cell]> = [];
      for (let r = 0; r < rect.height; r++) {
        for (let c = 0; c < rect.width; c++) {
          const cell = cells[cellKey(rect.x + c, rect.y + r)];
          if (cell) moved.push([cellKey(rect.x + c + dCol, rect.y + r + dRow), cell]);
        }
      }
      for (let r = 0; r < rect.height; r++) {
        for (let c = 0; c < rect.width; c++) {
          const sourceKey = cellKey(rect.x + c, rect.y + r);
          const destKey = cellKey(rect.x + c + dCol, rect.y + r + dRow);
          delete cells[sourceKey];
          delete cells[destKey];
          removals.push(`${sheet.id}!${sourceKey}`, `${sheet.id}!${destKey}`);
        }
      }
      for (const [key, cell] of moved) {
        cells[key] = cell;
        updates.push({ nodeId: `${sheet.id}!${key}`, src: cell.src });
      }
    }
    const swept: Record<string, Cell> = {};
    let changed = sheet.id === cutSheetId;
    for (const [key, cell] of Object.entries(cells)) {
      const src = retargetRefs(cell.src, rect, dCol, dRow, (refSheet) =>
        refSheet ? sheetNamesEqual(refSheet, cutSheetName) : sheet.id === cutSheetId,
      );
      if (src === cell.src) {
        swept[key] = cell;
      } else {
        swept[key] = { ...cell, src };
        updates.push({ nodeId: `${sheet.id}!${key}`, src });
        changed = true;
      }
    }
    if (!changed) return sheet;
    const used = sheet.id === cutSheetId ? usedExtent(swept) : sheet.used;
    return { ...sheet, cells: swept, used };
  });
  return { sheets: next, removals, updates };
}
